import React, { useCallback, useEffect, useState } from "react";
import { Users } from "lucide-react";
import EmptyState from "../EmptyState";
import { projectApi, errText } from "../../lib/projectAccess";
import TeamMemberRow from "./TeamMemberRow";
import AddMemberForm from "./AddMemberForm";

// B2b-2: Project → Team (personnel directory). Add/remove only shows for Founder/Admin/the managing PM; the backend
// enforces every rule again (no elevated roles from a PM, no self-change, Founder protected).
export default function ProjectTeamPanel({ projectId }) {
  const [data, setData] = useState(null);
  const [assignable, setAssignable] = useState(null);
  const [history, setHistory] = useState(false);
  const [err, setErr] = useState("");

  const load = useCallback(() => {
    setErr("");
    projectApi.personnel(projectId, history).then((r) => setData(r.data)).catch((e) => setErr(errText(e)));
    projectApi.assignable(projectId).then((r) => setAssignable(r.data)).catch(() => setAssignable(null));
  }, [projectId, history]);

  useEffect(() => { load(); }, [load]);

  if (err) return <div className="text-sm text-red-700" role="alert" data-testid="project-team-error">{err}</div>;
  if (!data) return <div className="font-mono text-xs text-erf-dark/60" data-testid="project-team-loading">Loading team…</div>;

  const canManage = Boolean(data.can_manage && assignable?.can_manage);
  return (
    <section className="space-y-4" data-testid="project-team" aria-labelledby="project-team-heading">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 id="project-team-heading" className="font-nav text-base font-semibold text-erf-dark">
          Team <span className="text-erf-dark/60 font-normal">({data.people.length})</span>
        </h2>
        {data.can_manage && (
          <label className="inline-flex items-center gap-2 min-h-[44px] text-xs font-nav text-erf-dark/80 cursor-pointer">
            <input type="checkbox" className="w-5 h-5 accent-erf-gold" checked={history}
              onChange={(e) => setHistory(e.target.checked)} data-testid="project-team-history-toggle" />
            Show removed members
          </label>
        )}
      </div>
      {canManage && <AddMemberForm projectId={projectId} assignable={assignable} people={data.people} onAdded={load} />}
      {data.people.length === 0 ? (
        <EmptyState icon={Users} title="No one is assigned yet"
          subtitle={canManage ? "Add the project manager, superintendent and crew above." : "The project manager adds the team."} />
      ) : (
        <ul className="grid gap-2" data-testid="project-team-list">
          {data.people.map((m) => (
            <TeamMemberRow key={m.assignment_id} projectId={projectId} member={m}
              canRemove={canManage && m.status !== "removed" && (assignable.allowed_roles || []).includes(m.role_on_project)}
              onRemoved={load} />
          ))}
        </ul>
      )}
    </section>
  );
}
